import { Button, InfoIcon, Modal, Select } from "@bp/ui-components";
import type { SelectOptionType } from "@bp/ui-components/dist/@bp/lib/Select/Select";
import { useState } from "react";
import type { MultiValue, SingleValue } from "react-select";
import { useMutation, useQuery } from "urql";
import { getFragmentData, graphql } from "../../../client/gql";
import { InfoBox } from "../../InfoBox/InfoBox";
import { showSuccessToast, showUserErrorToast } from "../../../utils/toast";
import { profileOptions, selectableProfiles } from "./linkProfileOptions";
import css from "./LinkProfileModal.module.scss";

const LinkProfileModal_Profile = graphql(`
  fragment LinkProfileModal_Profile on Profile {
    uuid
    firstName
    lastName
    email
    user {
      uuid
    }
  }
`);

const LinkProfileModal_Query = graphql(`
  query LinkProfileModal_Query($where: ProfileWhere) {
    profiles(where: $where, options: { sort: { lastName: ASC } }) {
      ...LinkProfileModal_Profile
    }
  }
`);

const LinkProfileModal_LinkProfile = graphql(`
  mutation LinkProfileModal_LinkProfile($where: ProfileWhere, $update: ProfileUpdateInput) {
    updateProfiles(where: $where, update: $update) {
      profiles {
        uuid
        user {
          uuid
        }
      }
    }
  }
`);

export type LinkTargetAccount = {
  uuid: string;
  /** Display name for the failed-toast context. */
  name: string;
};

type Props = {
  /** The account the chosen profile gets attached to. */
  account: LinkTargetAccount;
  /** Organization whose account-less profiles are offered. */
  orgUuid: string;
  onClose: () => void;
  onLinked: () => void;
};

/**
 * Attaches an existing profile *without* a login account to the given account.
 * Candidates are the account-less profiles of the organization the person page
 * is looking at; profiles that already have an account are not offered (they
 * would have to be detached first).
 */
export function LinkProfileModal({ account, orgUuid, onClose, onLinked }: Props) {
  const [profileUuid, setProfileUuid] = useState("");
  const [busy, setBusy] = useState(false);
  const [{ data, fetching }] = useQuery({
    query: LinkProfileModal_Query,
    variables: { where: { organization: { uuid: orgUuid } } },
  });
  const [, linkProfile] = useMutation(LinkProfileModal_LinkProfile);
  const profiles = getFragmentData(LinkProfileModal_Profile, data?.profiles ?? []);
  const options = profileOptions(profiles);
  const hasCandidates = selectableProfiles(profiles).length > 0;

  const link = async () => {
    if (!profileUuid) {
      return;
    }
    setBusy(true);
    const res = await linkProfile({
      where: { uuid: profileUuid },
      update: { user: { connect: { where: { node: { uuid: account.uuid } } } } },
    });
    setBusy(false);
    if (res.error) {
      showUserErrorToast({ text: "Verknüpfen fehlgeschlagen", error: res.error });
      return;
    }
    showSuccessToast(`Profil mit Konto „${account.name}" verknüpft`);
    onLinked();
  };

  return (
    <Modal
      isOpen
      ariaHideApp={false}
      hideFooter={false}
      width="s"
      title="Profil verknüpfen"
      onRequestClose={onClose}
      footer={
        <div className={css.foot}>
          <span className={css.footHint}>Nur Profile ohne Konto stehen zur Auswahl.</span>
          <div className={css.footActions}>
            <Button hierarchy="tertiary" onClick={onClose}>
              Abbrechen
            </Button>
            <Button
              hierarchy="primary"
              disabled={!profileUuid || busy || fetching}
              onClick={() => void link()}
            >
              Verknüpfen
            </Button>
          </div>
        </div>
      }
    >
      <div className={css.stack}>
        <Select
          name="profile"
          label="Bestehendes Profil"
          placeholder="Profil suchen …"
          options={options}
          value={options.find((o) => o.value === profileUuid) ?? null}
          isSearchable
          onChange={(opt: MultiValue<SelectOptionType> | SingleValue<SelectOptionType>) => {
            const value = (opt as SingleValue<SelectOptionType>)?.value;
            setProfileUuid(typeof value === "string" ? value : "");
          }}
        />
        {!fetching && !hasCandidates && (
          <InfoBox tone="warning" icon={<InfoIcon style={{ fontSize: 15 }} />}>
            <span style={{ fontSize: 12 }}>
              In dieser Organisation gibt es kein Profil ohne Konto.
            </span>
          </InfoBox>
        )}
        <InfoBox tone="primary" icon={<InfoIcon style={{ fontSize: 15 }} />}>
          <span style={{ fontSize: 12 }}>
            {`Das Profil erhält Login, Einladungen und Passwort-Reset über das Konto „${account.name}". Andere Profile des Kontos bleiben unverändert.`}
          </span>
        </InfoBox>
      </div>
    </Modal>
  );
}
